import React, { useMemo } from "react";
import { Order, CarDetailState, ProductDetail, ServiceDetail } from "../../types";
import ServiceTable from "./ServiceTable";

interface ServiceListProps {
  orderData: Order | null;
  categories: CarDetailState[];
}

// Calculate total price for a specific car
const calculateCarTotal = (services: ServiceDetail[]): number => {
  return services.reduce((total, service) => {
    const serviceTotal = typeof service.totalPrice === "string" ? parseFloat(service.totalPrice) : service.totalPrice;
    return total + (isNaN(serviceTotal) ? 0 : serviceTotal);
  }, 0);
};

// Calculate grand total safely
const calculateGrandTotal = (orderData: Order | null): string => {
  if (!orderData?.totalOrderPrice) return "0.00";

  const total = typeof orderData.totalOrderPrice === "string" ? parseFloat(orderData.totalOrderPrice) : orderData.totalOrderPrice;

  return isNaN(total) ? "0.00" : total.toFixed(2);
};

// Get all products from services for a specific car
const getProductsForCar = (services: ServiceDetail[]): ProductDetail[] => {
  return services.flatMap((service) => service.productDetails);
};

const ServiceList: React.FC<ServiceListProps> = ({ orderData, categories }) => {
  // Group services by car number
  const servicesByCar = useMemo(() => {
    if (!orderData?.serviceDetails?.length) return new Map<string, ServiceDetail[]>();

    const carMap = new Map<string, ServiceDetail[]>();

    orderData.serviceDetails.forEach((service) => {
      const carNumber = service.carNumber || "Unknown Car";
      if (!carMap.has(carNumber)) {
        carMap.set(carNumber, []);
      }
      carMap.get(carNumber)!.push(service);
    });

    return carMap;
  }, [orderData]);

  const carNumbers = Array.from(servicesByCar.keys());

  // 🟥 Empty case
  if (!orderData?.serviceDetails?.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Services by Car</h3>
        <div className="text-center py-6 text-gray-500">
          <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <p>No services found</p>
        </div>
      </div>
    );
  }

  // 🟨 No cars found — fallback to showing all services together
  if (carNumbers.length === 0) {
    const allProducts = orderData.serviceDetails.flatMap((s) => s.productDetails);

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Services by Car</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
          <p className="text-yellow-800 text-sm">Services found but no car information available. Showing all services together.</p>
        </div>
        <div className="max-h-98 overflow-auto">
          <ServiceTable products={allProducts} categories={categories} />
        </div>
      </div>
    );
  }

  // 🟩 Grouped by car
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 h-[73vh]">
      <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Services by Car</h3>

      <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
        {carNumbers.map((carNumber, carIndex) => {
          const carServices = servicesByCar.get(carNumber) || [];
          if (!carServices.length) return null;

          const products = getProductsForCar(carServices);
          const carTotal = calculateCarTotal(carServices);
          const displayCarNumber = carNumber || `Car ${carIndex + 1}`;

          return (
            <div key={carNumber || carIndex} className="border border-gray-200 rounded-lg p-3 hover:shadow transition bg-white">
              <div className="flex justify-between items-center mb-2 bg-gray-50 px-3 py-2 rounded-md">
                <h4 className="text-base font-medium text-gray-800">Car Number: {displayCarNumber}</h4>
                <div className="text-sm font-semibold text-green-600">Total: {carTotal.toFixed(2)}</div>
              </div>
              <ServiceTable products={products} categories={categories} />
            </div>
          );
        })}

        {/* Grand Total */}
        {carNumbers.length > 1 && (
          <div className="border-t border-gray-300 pt-3 mt-2 sticky bottom-0 bg-white">
            <div className="flex justify-between items-center text-lg font-bold text-gray-900">
              <span>Grand Total:</span>
              <span>{calculateGrandTotal(orderData)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceList;
